import type { ReactNode } from 'react'
import type { Appointment } from '@/types/clinic'

import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useClinic } from '@/lib/clinic/store'
import { byAppointmentTime } from '@/lib/clinic/selectors'
import { useLookups } from '@/hooks/use-clinic-helpers'
import AppointmentPopover from '@/components/appointments/appointment-popover'
import { BLOCK_TONES } from '@/components/appointments/calendar-time-grid'

import {
    addDays,
    dayOfWeek,
    eachDay,
    endOfMonth,
    formatDate,
    formatTime,
    monthKey,
    parseISODate,
    startOfMonth,
    startOfWeek
} from '@/lib/dates'

import { APPOINTMENT_STATUS_META, WEEKDAYS } from '@/constants/clinic'

interface CalendarMonthProps {
    anchor: string
    appointments: Appointment[]
    onSelectDay: (date: string) => void
    onBook: (date: string) => void
}

const CalendarMonth = ({ anchor, appointments, onSelectDay, onBook }: CalendarMonthProps): ReactNode => {
    const { data, today } = useClinic()
    const { patientsById } = useLookups()
    const settings = data.settings.appointments

    const first = startOfMonth(anchor)
    const gridStart = startOfWeek(first)
    const gridEnd = addDays(startOfWeek(endOfMonth(anchor)), 6)
    const days = eachDay(gridStart, gridEnd)

    return (
        <div className='overflow-x-auto'>
            <div className='min-w-[640px]'>
                <div className='grid grid-cols-7 border-b bg-muted/40'>
                    {WEEKDAYS.map(day => (
                        <div key={day.value} className='px-2 py-2 text-center text-xs font-semibold tracking-wide text-muted-foreground uppercase'>
                            {day.short}
                        </div>
                    ))}
                </div>
                <div className='grid grid-cols-7'>
                    {days.map(day => {
                        const inMonth = monthKey(day) === monthKey(anchor)
                        const closed = !settings.workingDays.includes(dayOfWeek(day))
                        const isToday = day === today
                        const items = appointments.filter(item => item.date === day).sort(byAppointmentTime)
                        const shown = items.slice(0, 3)
                        return (
                            <div
                                key={day}
                                className={cn(
                                    'group relative min-h-28 border-r border-b p-1.5 last:border-r-0 [&:nth-child(7n)]:border-r-0',
                                    !inMonth && 'bg-muted/30',
                                    closed && inMonth && 'bg-muted/50'
                                )}
                            >
                                <div className='flex items-center justify-between'>
                                    <button
                                        type='button'
                                        onClick={() => onSelectDay(day)}
                                        className={cn(
                                            'flex size-7 items-center justify-center rounded-full text-xs font-semibold transition-colors hover:bg-accent focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
                                            isToday && 'bg-primary text-primary-foreground hover:bg-primary/90',
                                            !isToday && !inMonth && 'text-muted-foreground/60',
                                            !isToday && inMonth && 'text-foreground'
                                        )}
                                        aria-label={`Open ${formatDate(day)}, ${items.length} appointments`}
                                    >
                                        {parseISODate(day).getDate()}
                                    </button>
                                    {!closed && (
                                        <button
                                            type='button'
                                            onClick={() => onBook(day)}
                                            className='flex size-6 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:bg-accent hover:text-foreground focus-visible:opacity-100 focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'
                                            aria-label={`Book on ${formatDate(day)}`}
                                        >
                                            <Plus className='size-3.5' aria-hidden='true' />
                                        </button>
                                    )}
                                </div>

                                <div className='mt-1 grid gap-1'>
                                    {shown.map(appointment => {
                                        const patient = patientsById.get(appointment.patientId)
                                        const tone = APPOINTMENT_STATUS_META[appointment.status].tone
                                        return (
                                            <AppointmentPopover
                                                key={appointment.id}
                                                appointment={appointment}
                                                trigger={(
                                                    <button
                                                        type='button'
                                                        className={cn(
                                                            'w-full truncate rounded border-l-2 px-1.5 py-0.5 text-left text-[11px] font-medium transition-colors focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
                                                            BLOCK_TONES[tone]
                                                        )}
                                                    />
                                                )}
                                            >
                                                <span className='tabular-nums'>{formatTime(appointment.time).replace(' ', '').toLowerCase()}</span> {patient?.name ?? 'Patient'}
                                            </AppointmentPopover>
                                        )
                                    })}
                                    {items.length > shown.length && (
                                        <button
                                            type='button'
                                            onClick={() => onSelectDay(day)}
                                            className='rounded px-1.5 text-left text-[11px] font-medium text-primary hover:underline'
                                        >
                                            +{items.length - shown.length} more
                                        </button>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default CalendarMonth
